import { useSelector, useDispatch } from 'react-redux';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { PolicyTable } from 'components/Tables/PolicyTable';
import { getPolicyAction } from 'store/actions/propertyActions';
import { getPolicy } from 'store/sagas/APIs/property';
import { fetchAllPaginated } from 'utils/exportPagination';
import { Loading } from '@leapeasy/ui-kit';
import { PolicyListDropFilterFields } from 'components/PolicyList/PolicyListDropFilterFields';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import { buildBuildingBreadcrumbParent, withBreadcrumbParents } from 'utils/breadcrumbNavigation';
import { buildDetailNavigationState } from 'utils/detailBackNavigation';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildPolicyListFilterChips,
  parsePolicyListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import {
  DEFAULT_POLICY_LIST_EXTRA_FILTERS,
  POLICY_LIST_URL_PARAM_KEYS,
  areArraysEqual,
  areDateRangesEqual,
  arePolicyIssuedDateRangeFiltersEqual,
  buildPolicyListRequestFilter,
  normalizeArrayFilter,
  normalizeDateRangeFilter,
  normalizePolicyIssuedDateRangeFilter,
  parsePoliciesExtraFilters,
  serializePoliciesFilters,
} from 'utils/policyListQueryFilters';
import { EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER } from 'utils/policyIssuedDateRangeFilter';
import {
  POLICY_COVERAGE_STATUS_FILTER_OPTIONS,
  POLICY_LIST_APP_TYPE_FILTER_OPTIONS,
} from 'data/constants/common_constants';

const EMPTY_POLICY_LIST_FILTER = {
  searchText: '',
  options: {},
  selectedAppType: [],
  selectedIssuedDate: { ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER },
  selectedCoverageStatus: [],
  selectedLeaseDate: { startDate: null, endDate: null },
  selectedTenantCount: [],
  activeLease: [],
  selectedExpiringRange: null,
};

export const PolicyTab = ({ building }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousBuildingIdRef = useRef(undefined);

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Policy..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_POLICY_LIST_EXTRA_FILTERS,
    parseExtraFilters: parsePoliciesExtraFilters,
    serializeExtraFilters: serializePoliciesFilters,
  });

  const [selectedAppType, setSelectedAppType] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedIssuedDate, setSelectedIssuedDate] = useState(() =>
    normalizePolicyIssuedDateRangeFilter(extraFilters?.selectedIssuedDate),
  );
  const [selectedCoverageStatus, setSelectedCoverageStatus] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedCoverageStatus),
  );
  const [selectedLeaseDate, setSelectedLeaseDate] = useState(() =>
    normalizeDateRangeFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedTenantCount, setSelectedTenantCount] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedTenantCount),
  );
  const [activeLease, setActiveLease] = useState(() =>
    normalizeArrayFilter(extraFilters?.activeLease),
  );
  const [selectedAppTypeDraft, setSelectedAppTypeDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedIssuedDateDraft, setSelectedIssuedDateDraft] = useState(() =>
    normalizePolicyIssuedDateRangeFilter(extraFilters?.selectedIssuedDate),
  );
  const [selectedCoverageStatusDraft, setSelectedCoverageStatusDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedCoverageStatus),
  );
  const [selectedLeaseDateDraft, setSelectedLeaseDateDraft] = useState(() =>
    normalizeDateRangeFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedTenantCountDraft, setSelectedTenantCountDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedTenantCount),
  );
  const [activeLeaseDraft, setActiveLeaseDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.activeLease),
  );
  const [selectedExpiringRange, setSelectedExpiringRange] = useState(
    () => extraFilters?.selectedExpiringRange || null,
  );
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedAppType = normalizeArrayFilter(extraFilters.selectedAppType);
    const nextSelectedIssuedDate = normalizePolicyIssuedDateRangeFilter(
      extraFilters.selectedIssuedDate,
    );
    const nextSelectedCoverageStatus = normalizeArrayFilter(extraFilters.selectedCoverageStatus);
    const nextSelectedLeaseDate = normalizeDateRangeFilter(extraFilters.selectedLeaseDate);
    const nextSelectedTenantCount = normalizeArrayFilter(extraFilters.selectedTenantCount);
    const nextActiveLease = normalizeArrayFilter(extraFilters.activeLease);
    const nextSelectedExpiringRange = extraFilters?.selectedExpiringRange || null;
    setSelectedAppType((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedIssuedDate((prev) =>
      arePolicyIssuedDateRangeFiltersEqual(prev, nextSelectedIssuedDate)
        ? prev
        : nextSelectedIssuedDate,
    );
    setSelectedCoverageStatus((prev) =>
      areArraysEqual(prev, nextSelectedCoverageStatus) ? prev : nextSelectedCoverageStatus,
    );
    setSelectedLeaseDate((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedTenantCount((prev) =>
      areArraysEqual(prev, nextSelectedTenantCount) ? prev : nextSelectedTenantCount,
    );
    setActiveLease((prev) => (areArraysEqual(prev, nextActiveLease) ? prev : nextActiveLease));
    setSelectedAppTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedIssuedDateDraft((prev) =>
      arePolicyIssuedDateRangeFiltersEqual(prev, nextSelectedIssuedDate)
        ? prev
        : nextSelectedIssuedDate,
    );
    setSelectedCoverageStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedCoverageStatus) ? prev : nextSelectedCoverageStatus,
    );
    setSelectedLeaseDateDraft((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedTenantCountDraft((prev) =>
      areArraysEqual(prev, nextSelectedTenantCount) ? prev : nextSelectedTenantCount,
    );
    setActiveLeaseDraft((prev) => (areArraysEqual(prev, nextActiveLease) ? prev : nextActiveLease));
    setSelectedExpiringRange((prev) =>
      prev === nextSelectedExpiringRange ? prev : nextSelectedExpiringRange,
    );
  }, [extraFilters]);

  useLayoutEffect(() => {
    const id = building?.building_id;
    if (!id) return;
    if (previousBuildingIdRef.current !== id) {
      if (previousBuildingIdRef.current !== undefined) {
        const next = new URLSearchParams(searchParams);
        POLICY_LIST_URL_PARAM_KEYS.forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
      }
      previousBuildingIdRef.current = id;
    }
  }, [building?.building_id, searchParams, setSearchParams]);

  const handleDispatch = useCallback(() => {
    if (!building?.building_id) return;
    dispatch(
      getPolicyAction({
        filter: buildPolicyListRequestFilter({
          filter,
          selectedAppType,
          selectedIssuedDate,
          selectedCoverageStatus,
          selectedLeaseDate,
          selectedTenantCount,
          activeLease,
          selectedExpiringRange,
        }),
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions,
        role: 'building',
        id: building.building_id,
      }),
    );
  }, [
    dispatch,
    building.building_id,
    filter,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
    selectedAppType,
    selectedIssuedDate,
    selectedCoverageStatus,
    selectedLeaseDate,
    selectedTenantCount,
    activeLease,
    selectedExpiringRange,
  ]);

  useEffect(() => {
    handleDispatch();
  }, [handleDispatch]);

  const successMsg = useSelector((state) => state.getIn(['notification', 'successMsg']));
  const successText = successMsg == null ? '' : String(successMsg);

  useEffect(() => {
    if (!successText || successText === 'loading') return;
    if (!successText.toLowerCase().includes('cancelled')) return;
    handleDispatch();
  }, [successText, handleDispatch]);

  const APPTYPE = POLICY_LIST_APP_TYPE_FILTER_OPTIONS;
  const COVERAGE_STATUS = POLICY_COVERAGE_STATUS_FILTER_OPTIONS;

  const policies = useSelector((state) => state.getIn(['property', 'policy']));
  const policiesJSON = policies && policies.toJS();
  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(policiesJSON?.total) || 0,
    isLoading: Boolean(policiesJSON?.isLoading),
  });

  useEffect(() => {
    if (!building?.building_id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getPolicy({
          filter: EMPTY_POLICY_LIST_FILTER,
          offset: 0,
          limit: 1,
          sortOptions,
          role: 'building',
          id: building.building_id,
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions, building?.building_id]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(policiesJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getPolicy({
            filter: buildPolicyListRequestFilter({
              filter,
              selectedAppType,
              selectedIssuedDate,
              selectedCoverageStatus,
              selectedLeaseDate,
              selectedTenantCount,
              activeLease,
              selectedExpiringRange,
            }),
            offset,
            limit,
            sortOptions,
            role: 'building',
            id: building.building_id,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export policies right now.');
    }
  }, [
    policiesJSON?.total,
    filter,
    selectedAppType,
    selectedIssuedDate,
    selectedCoverageStatus,
    selectedLeaseDate,
    selectedTenantCount,
    activeLease,
    selectedExpiringRange,
    sortOptions,
    building.building_id,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords = Number(fullExportDatasetTotal) || Number(policiesJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getPolicy({
            filter: EMPTY_POLICY_LIST_FILTER,
            offset,
            limit,
            sortOptions,
            role: 'building',
            id: building.building_id,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export policies right now.');
    }
  }, [fullExportDatasetTotal, policiesJSON?.total, sortOptions, building.building_id]);

  const resetFunc = useCallback(() => {
    setSelectedAppType([]);
    setSelectedIssuedDate({ ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER });
    setSelectedCoverageStatus([]);
    setSelectedLeaseDate({ startDate: null, endDate: null });
    setSelectedTenantCount([]);
    setActiveLease([]);
    setSelectedAppTypeDraft([]);
    setSelectedIssuedDateDraft({ ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER });
    setSelectedCoverageStatusDraft([]);
    setSelectedLeaseDateDraft({ startDate: null, endDate: null });
    setSelectedTenantCountDraft([]);
    setActiveLeaseDraft([]);
    setSelectedExpiringRange(null);
    setExtraFilters({ ...DEFAULT_POLICY_LIST_EXTRA_FILTERS });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const applyFunc = useCallback(() => {
    setSelectedAppType(selectedAppTypeDraft);
    setSelectedIssuedDate(selectedIssuedDateDraft);
    setSelectedCoverageStatus(selectedCoverageStatusDraft);
    setSelectedLeaseDate(selectedLeaseDateDraft);
    setSelectedTenantCount(selectedTenantCountDraft);
    setActiveLease(activeLeaseDraft);
    setExtraFilters({
      selectedAppType: selectedAppTypeDraft,
      selectedIssuedDate: selectedIssuedDateDraft,
      selectedCoverageStatus: selectedCoverageStatusDraft,
      selectedLeaseDate: selectedLeaseDateDraft,
      selectedTenantCount: selectedTenantCountDraft,
      activeLease: activeLeaseDraft,
      selectedExpiringRange,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedAppTypeDraft,
    selectedIssuedDateDraft,
    selectedCoverageStatusDraft,
    selectedLeaseDateDraft,
    selectedTenantCountDraft,
    activeLeaseDraft,
    selectedExpiringRange,
    setExtraFilters,
    setPagination,
  ]);

  const listFilterChips = useMemo(
    () =>
      buildPolicyListFilterChips({
        selectedAppType,
        selectedIssuedDate,
        selectedCoverageStatus,
        selectedLeaseDate,
        selectedTenantCount,
        activeLease,
        selectedExpiringRange,
        appTypeOptions: APPTYPE,
        coverageStatusOptions: COVERAGE_STATUS,
      }),
    [
      selectedAppType,
      selectedIssuedDate,
      selectedCoverageStatus,
      selectedLeaseDate,
      selectedTenantCount,
      activeLease,
      selectedExpiringRange,
      APPTYPE,
      COVERAGE_STATUS,
    ],
  );

  const handleRemovePolicyTabFilterChip = useCallback(
    (chipId) => {
      const parsed = parsePolicyListFilterChipId(chipId);
      if (!parsed) return;
      if (parsed.type === 'issued') {
        const cleared = { ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER };
        setSelectedIssuedDate(cleared);
        setSelectedIssuedDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedIssuedDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }
      if (parsed.type === 'coverageStatus') {
        const next = selectedCoverageStatus.filter((s) => s !== parsed.value);
        setSelectedCoverageStatus(next);
        setSelectedCoverageStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedCoverageStatus: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }
      if (parsed.type === 'lease') {
        const cleared = { startDate: null, endDate: null };
        setSelectedLeaseDate(cleared);
        setSelectedLeaseDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedLeaseDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }
      if (parsed.type === 'expiring') {
        setSelectedExpiringRange(null);
        setExtraFilters((prev) => ({ ...prev, selectedExpiringRange: null }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }
      if (parsed.type === 'appType') {
        const next = selectedAppType.filter((s) => s !== parsed.value);
        setSelectedAppType(next);
        setSelectedAppTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedAppType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }
      if (parsed.type === 'tenant') {
        const next = selectedTenantCount.filter((s) => s !== parsed.value);
        setSelectedTenantCount(next);
        setSelectedTenantCountDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedTenantCount: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }
      if (parsed.type === 'activeLease') {
        const next = activeLease.filter((s) => s !== parsed.value);
        setActiveLease(next);
        setActiveLeaseDraft(next);
        setExtraFilters((prev) => ({ ...prev, activeLease: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [
      selectedAppType,
      selectedCoverageStatus,
      selectedTenantCount,
      activeLease,
      setExtraFilters,
      setPagination,
    ],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemovePolicyTabFilterChip,
    resetFunc,
  );

  const onClickPolicy = useCallback(
    (policy) => {
      if (!policy?.app_id) {
        dispatch(failedNotif('Policy detail ID is missing.'));
        return;
      }
      const buildingParent = buildBuildingBreadcrumbParent(
        building,
        building?.building_id,
        'policy',
      );
      const returnTo = `${location.pathname}${location.search}`;
      dispatch(
        openDetails({
          type: 'policy',
          data: policy,
        }),
      );
      navigate(`/property/policies/${policy.app_id}/detail`, {
        state: buildDetailNavigationState(
          withBreadcrumbParents(buildingParent ? [buildingParent] : []),
          returnTo,
        ),
      });
    },
    [dispatch, navigate, building, location.pathname, location.search],
  );

  return (
    <Box>
      {policies ? (
        <React.Fragment>
          <CustomBox>
            {policiesJSON.isLoading && (
              <LoadingEffect>
                <Loading size={'large'} />
              </LoadingEffect>
            )}
          </CustomBox>
          <PolicyTable
            policies={policiesJSON.data}
            onRequestExportData={handleRequestExportData}
            onRequestFullExportData={handleRequestFullExportData}
            exportFullDatasetTotal={fullExportDatasetTotal}
            dropFilterActiveSummary={dropFilterActiveSummary}
            dropFilterActiveCount={dropFilterActiveCount}
            filter={filter}
            dropFilter={{
              component: (
                <PolicyListDropFilterFields
                  appTypeOptions={[...APPTYPE]}
                  coverageStatusOptions={[...COVERAGE_STATUS]}
                  selectedAppTypeDraft={selectedAppTypeDraft}
                  onAppTypeDraftChange={setSelectedAppTypeDraft}
                  selectedIssuedDateDraft={selectedIssuedDateDraft}
                  onIssuedDateDraftChange={setSelectedIssuedDateDraft}
                  selectedCoverageStatusDraft={selectedCoverageStatusDraft}
                  onCoverageStatusDraftChange={setSelectedCoverageStatusDraft}
                  selectedLeaseDateDraft={selectedLeaseDateDraft}
                  onLeaseDateDraftChange={setSelectedLeaseDateDraft}
                  selectedTenantCountDraft={selectedTenantCountDraft}
                  onTenantCountDraftChange={setSelectedTenantCountDraft}
                  activeLeaseDraft={activeLeaseDraft}
                  onActiveLeaseDraftChange={setActiveLeaseDraft}
                />
              ),
              resetFunc: resetFunc,
              applyFunc: applyFunc,
            }}
            onChangeFilter={setFilter}
            pagination={{
              ...pagination,
              totalItems: policiesJSON.total,
              totalPages: Math.ceil(policiesJSON.total / pagination.rowsPerPage),
            }}
            onChangePagination={setPagination}
            sortOptions={sortOptions}
            onChangeSort={setSortOptions}
            onClickPolicy={onClickPolicy}
          />
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </Box>
  );
};

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;
